"use client";

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { usePathname } from "next/navigation";

import { authenticatedRawFetch } from "@/lib/api/client";
import { getStreamProjectSearchRunsUrl } from "@/lib/api/generated";
import {
  isTerminalAuthError,
  parseSSE,
} from "@/features/jobs/hooks/use-job-pipeline-events";
import {
  SAVED_SEARCH_QUERY_PREFIX,
  projectSearchesQueryKey,
  savedSearchQueryKey,
} from "@/hooks/use-search";
import { useSearchRunsStore, type RunEvent } from "@/stores/search-runs-store";
import { showToast } from "@/components/ui/sonner";

const MAX_ATTEMPTS = 10;
// The server emits a `: keep-alive` comment every ~15s when idle. Anything
// quieter than 3x that is treated as a dead connection and reconnected.
const HEARTBEAT_TIMEOUT_MS = 45_000;

const RUN_EVENT_TYPES: ReadonlySet<string> = new Set([
  "run.phase",
  "run.ready",
  "run.failed",
  "run.cancelled",
]);

/**
 * Prefix for a saved search's ranked results cache. Task 26's
 * `useSearchResults` keys its pages under this prefix so one invalidation here
 * refreshes every loaded page.
 */
export function searchResultsQueryKey(searchId: string) {
  return ["search-results", searchId] as const;
}

/** `RunEvent` minus `type`, which travels on the SSE `event:` line. */
type RunEventPayload = Omit<RunEvent, "type">;

function isRunEventPayload(value: unknown): value is RunEventPayload {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.search_id === "string" && typeof v.run_id === "string";
}

/** True when `pathname` is the detail page (or a sub-route) of `searchId`. */
function isViewingSearch(pathname: string | null, searchId: string): boolean {
  if (!pathname) return false;
  const base = `/talent-search/${searchId}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * Subscribes to `GET /api/projects/{projectId}/search-runs/stream` (SSE), one
 * connection per active project, and feeds every `run.*` event into the runs
 * store, which is the single source of truth for run phase in the UI. On a
 * terminal event (`run.ready` / `run.failed` / `run.cancelled`) it also
 * invalidates the search's results, its saved-search payload and the
 * project's search list. The stream carries every project member's runs, so
 * ready / failed only toast for a run this client started (`ownRuns`) and
 * only when the user is not already looking at that search (a cancel is the
 * user's own doing: no toast).
 *
 * Reconnects with capped exponential backoff; a heartbeat watchdog forces an
 * immediate reconnect if the stream goes silent. The server replays in-flight
 * runs on connect, so a reconnect only needs the project list and saved
 * searches refetched to pick up runs that finished while we were away.
 *
 * A stream that gave up (backoff exhausted, or a 401 while the session was
 * being refreshed) is not dead for good: coming back `online` or returning to
 * the tab restarts it with a fresh attempt budget; `online` also drops a
 * connection that still looks open (a half-open socket after a network change
 * would otherwise only be caught by the watchdog) and reconnects at once.
 * `connected` is published on the runs store (`streamConnected`, with the
 * project it covers as `streamProjectId`) so the results provider can fall
 * back to polling the saved search while a run is in flight without a live
 * stream for its project.
 */
export function useProjectSearchRunsStream(projectId: string | null): {
  connected: boolean;
} {
  const connected = useSearchRunsStore((s) => s.streamConnected);
  const setConnected = useSearchRunsStore((s) => s.setStreamConnected);
  const qc = useQueryClient();
  const applyEvent = useSearchRunsStore((s) => s.applyEvent);

  // Read the current route from a ref inside the stream loop so the toast
  // suppression follows client-side navigation without restarting the stream.
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    // No project: nothing to stream. `connected` is already false here, either
    // from the initial state or from the previous project's cleanup below.
    if (!projectId) return;
    const pid: string = projectId;

    let cancelled = false;
    let attempt = 0;
    let hasConnected = false;
    let isConnected = false;
    // True while `run()` is executing (connected, reading, or backing off).
    let loopAlive = false;
    let backoffTimer: ReturnType<typeof setTimeout> | null = null;
    // Resolves the pending backoff wait early (wake-up on `online` / focus).
    let skipBackoff: (() => void) | null = null;
    let activeController: AbortController | null = null;
    // Set when `online` aborts a live connection so the loop skips the backoff.
    let reconnectRequested = false;

    const publishConnected = (value: boolean) => {
      isConnected = value;
      setConnected(value, value ? pid : null);
    };

    const onRunEvent = (type: RunEvent["type"], data: RunEventPayload) => {
      applyEvent({ ...data, type });
      if (type === "run.phase") return;

      // On ready, the new run id enters the results query key, which fetches
      // the new page by itself; only mark the prefix stale so the superseded
      // run's page is not refetched in the meantime. A failed / cancelled run
      // keeps the earlier ready run's key, so its page refreshes as usual.
      void qc.invalidateQueries({
        queryKey: searchResultsQueryKey(data.search_id),
        ...(type === "run.ready" ? { refetchType: "none" as const } : {}),
      });
      void qc.invalidateQueries({
        queryKey: savedSearchQueryKey(data.search_id),
      });
      void qc.invalidateQueries({ queryKey: projectSearchesQueryKey(pid) });

      // A superseded run's late terminal event (a re-run cancels the old run)
      // must not consume the marker the newer run's finish will need.
      const runs = useSearchRunsStore.getState();
      if (runs.runs[data.search_id]?.runId !== data.run_id) return;
      const own = runs.releaseOwnRun(data.search_id);
      if (type === "run.cancelled" || !own) return;
      if (isViewingSearch(pathnameRef.current, data.search_id)) return;
      if (type === "run.ready") {
        showToast.success("Search ready", {
          description: "Your candidates are ranked.",
        });
      } else {
        showToast.error("Search failed", {
          description: data.error?.message ?? "Please retry the search.",
        });
      }
    };

    async function run() {
      loopAlive = true;
      try {
        while (!cancelled) {
          const controller = new AbortController();
          activeController = controller;
          let watchdog: ReturnType<typeof setTimeout> | null = null;
          let watchdogTripped = false;
          const bumpWatchdog = () => {
            if (watchdog) clearTimeout(watchdog);
            watchdog = setTimeout(() => {
              watchdogTripped = true;
              controller.abort();
            }, HEARTBEAT_TIMEOUT_MS);
          };

          try {
            const res = await authenticatedRawFetch(
              getStreamProjectSearchRunsUrl(pid),
              { method: "GET", signal: controller.signal, cache: "no-store" },
            );
            if (!res.ok || !res.body) {
              if (res.status === 401) {
                publishConnected(false);
                return;
              }
              throw new Error(`stream failed: ${res.status}`);
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            bumpWatchdog();

            while (!cancelled) {
              const { done, value } = await reader.read();
              if (done) break;
              // Any byte, event or `: keep-alive`, proves the stream is alive.
              bumpWatchdog();
              buffer += decoder.decode(value, { stream: true });
              const parsed = parseSSE(buffer);
              buffer = parsed.rest;
              for (const e of parsed.events) {
                if (e.event === "stream_ready") {
                  attempt = 0;
                  publishConnected(true);
                  // Events emitted while we were disconnected are gone; the
                  // server only replays runs still in flight, so refetch the
                  // list and every saved search to converge on any run that
                  // finished meanwhile.
                  if (hasConnected) {
                    void qc.invalidateQueries({
                      queryKey: projectSearchesQueryKey(pid),
                    });
                    void qc.invalidateQueries({
                      queryKey: SAVED_SEARCH_QUERY_PREFIX,
                    });
                  }
                  hasConnected = true;
                  continue;
                }
                if (!RUN_EVENT_TYPES.has(e.event)) continue;
                let data: unknown;
                try {
                  data = JSON.parse(e.data);
                } catch {
                  continue; // unparseable payload: skip, keep the stream
                }
                if (!isRunEventPayload(data)) continue;
                onRunEvent(e.event as RunEvent["type"], data);
              }
            }

            // Server closed the stream cleanly; fall through to reconnect.
            publishConnected(false);
          } catch (err) {
            if ((err as Error).name === "AbortError") {
              // Unmount teardown (cancelled) or a watchdog-forced reconnect.
              if (cancelled) return;
              publishConnected(false);
            } else if (isTerminalAuthError(err)) {
              publishConnected(false);
              return;
            } else {
              publishConnected(false);
            }
          } finally {
            if (watchdog) clearTimeout(watchdog);
          }

          if (cancelled) return;
          attempt++;
          if (attempt > MAX_ATTEMPTS) return;
          // Forced reconnects (watchdog, `online`) retry immediately; network
          // errors back off.
          const delay =
            watchdogTripped || reconnectRequested
              ? 0
              : Math.min(15_000, 1_000 * 2 ** (attempt - 1));
          reconnectRequested = false;
          await new Promise<void>((resolve) => {
            skipBackoff = () => {
              if (backoffTimer) clearTimeout(backoffTimer);
              backoffTimer = null;
              skipBackoff = null;
              resolve();
            };
            backoffTimer = setTimeout(() => {
              backoffTimer = null;
              skipBackoff = null;
              resolve();
            }, delay);
          });
        }
      } finally {
        loopAlive = false;
      }
    }

    // The network came back or the tab is visible again: a stream that gave
    // up gets a fresh attempt budget, one mid-backoff retries right away. A
    // stream that still looks connected is left alone on a plain visibility
    // change, but `online` drops it: after a network change the socket may be
    // half-open, which only the heartbeat watchdog would notice, 45s later.
    // The server replays in-flight runs on connect, so nothing is lost.
    const wake = ({ forceReconnect = false } = {}) => {
      if (cancelled) return;
      if (isConnected) {
        if (forceReconnect) {
          reconnectRequested = true;
          activeController?.abort();
        }
        return;
      }
      attempt = 0;
      if (!loopAlive) {
        void run();
      } else {
        skipBackoff?.();
      }
    };
    const onOnline = () => wake({ forceReconnect: true });
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") wake();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibilityChange);

    void run();

    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      activeController?.abort();
      if (backoffTimer) clearTimeout(backoffTimer);
      publishConnected(false);
    };
  }, [projectId, qc, applyEvent, setConnected]);

  return { connected };
}
